import { CoffeeLoyalty } from "redux_store/loyalty/models";

import { UpsellSource } from "../../../modules/Upsell/model";
import {
  AssociatedOffer,
  DefaultModifier,
  Modifier,
  ProductDiscountsApplied,
} from "../../../redux_store/cart/models";
import {
  OrderFinalisationStatusType,
  OrderFulfilmentStatusType,
  OrderProgressStatus,
} from "../../../redux_store/checkout/model";
import {
  CollectionType,
  DeliveryOrder,
  OrderType,
} from "../../../redux_store/order/models";
import { VersionAndPlatformData } from "../../httpClient";
import { TagLookup } from "../menu/model";
import { OrderOffset } from "../store/model";

export interface ItemPayload {
  id: number;
  name: string;
  miamGUID?: string;
  upsellSource?: string;
  quantity: number;
  removeModifiers?: number[];
  addModifiers?: number[];
  extraModifiers?: number[];
  addFillings?: number[];
  associatedOffers?: AssociatedOffer[];
  productDiscountsApplied?: ProductDiscountsApplied[];
  parts?: ItemPayload[];
  menuFlowId?: number;
}
export interface GetOrderPayload {
  sessionId: string;
}

export interface CreateOrderPayload extends VersionAndPlatformData {
  storeId: number;
  basketItems: ItemPayload[];
  collectionType: CollectionType;
  delivery: DeliveryOrder | null;
  pickUpTime: string;
  posMenuId: number;
  tableNumber?: string;
  coupons?: {
    code: string;
  }[];
  gygDollars?: {
    amount: number;
  };
  type?: OrderType;
}

export interface UpdateOrderPayload extends VersionAndPlatformData {
  orderId: string;
  orderPayload: CreateOrderPayload;
  orderTimeUpdated?: boolean;
}

export interface OrderResponse {
  activeUntil: string;
  basket: Basket;
  orderTimes: OrderTimes[];
  orderNumber: string;
  orderId: string;
  channelId: number;
  loyalty: BasketVerificationLoyalty;
  gygDollars?: GYGDollars;
  errors?: OrderErrorResponse[];
  pickUpTime?: string;
  orderCollectionType?: CollectionType;
  orderFinalisationStatus?: OrderFinalisationStatusType;
  orderFulfilmentStatus?: OrderFulfilmentStatusType;
  orderOffset: OrderOffset[];
  orderProgressStatus?: OrderProgressStatus;
  delivery?: DeliveryOrder;
}

export interface UpdateOrderResponse {
  orderResponse: OrderResponse;
  orderTimeUpdated: boolean;
}

export interface OrderErrorResponse {
  error: number;
  errorDescription: string;
}

export interface BasketVerificationLoyalty {
  actualDollarsBalance: number;
  actualPointsBalance: number;
  cardNumber: string;
  loyaltyRewardAmount: number;
  loyaltyRewardConversionLimit: number;
  accruedPoints: number;
  convertedDollars: number;
  afterCheckoutDollarsBalance: number;
  afterCheckoutPointsBalance: number;
  totalBonusPoints: number;
  totalBonusDollars: number;
}

export interface GYGDollars {
  applied: number;
  balance: number;
  errors: OrderErrorResponse[];
  status: string;
}

export enum LoyaltyBonusType {
  GYGDollar = "GYGDOLLAR",
  GYGPoints = "GYGPOINTS",
}

export interface ChildItem {
  id: number;
  productId: number;
  name: string;
  childItems: ChildItem[];
  quantity: number;
  unitPrice: number;
  price: number;
  isFavourite: boolean;
}

export interface BasketItem {
  id: number;
  name: string;
  miamGUID?: string;
  quantity: number;
  unitPrice: number;
  price: number;
  removeModifiers?: Modifier[];
  addModifiers?: Modifier[];
  extraModifiers?: Modifier[];
  addFillings?: Modifier[];
  defaultModifiers?: DefaultModifier[];
  parts?: BasketItem[];
  menuFlowId?: number;
  posPlu?: number;
  category?: string;
  displayText?: string;
  originalPrice?: number;
  tags?: string[];
  upsellSource?: UpsellSource;
  associatedOffers?: AssociatedOffer[];
  productDiscountsApplied?: ProductDiscountsApplied[];
  isOfferProduct?: boolean;
  isValid?: boolean;
  containerId?: number;
  posPageNo?: number;
  pageIdentifier?: number;
}

export interface DeliveryAvailability {
  deliveryAvailable: boolean;
  deliveryFeeItemAdded: boolean;
  deliveryFee?: unknown;
  deliveryDistance?: unknown;
  storeDeliveryRadius?: unknown;
  minBasketSize?: unknown;
  deliveryErrorMessage: string;
  deliveryAccepted: boolean;
}

export enum BasketCouponType {
  LoyaltyCoffee = "FREE_ITEM_BY_SECTION_ID",
}
export interface BasketCoupon {
  code: string;
  errors: [
    {
      error: number;
      errorDescription: string;
    },
  ];
  couponType?: BasketCouponType;
}

export interface Basket {
  coupons?: BasketCoupon[];
  tagLookup: TagLookup[];
  basketItems: BasketItem[];
  salesTax: number; // US tax
  taxRate: number;
  totalIncl: number; // total order with tax
  totalExcl: number; // total order without tax
  surcharges?: Surcharge[];
  coffeeLoyalty?: CoffeeLoyalty;
  discountApplied: boolean;
  discountAmount: number;
  totalBeforeDiscount: number;
  loyalty?: unknown;
  voucherCode?: unknown;
  storeInvalidProducts: BasketItem[];
  errors?: [
    {
      error: number;
      errorDescription: string;
    },
  ];
  validation?: {
    maximum: true;
    minimum: true;
    checkout: true;
  };
  basketValue: number;
  total: number;
  subtotal: number;
}

export interface OrderTimes {
  date: string;
  timeSlots: string[];
}

export interface PickupTimeOffsets {
  periodStart: string; //"2023-05-13T12:00:00", For readability in the Datadog logs
  periodEnd: string; //"2023-05-13T13:59:59", For readability in the Datadog logs
  offset: number; //4
}

export interface GetOrderByOrderIdPayload {
  orderId: string;
}

export interface GetOrderOffsetPayload {
  offset?: number;
}

export interface GetOrderResponse {
  orderId: string;
  timeSubmitted: string;
  pickUpTime: string;
  activeOrderUntil: string;
  orderFinalisationStatus: OrderFinalisationStatusType;
  orderFulfilmentStatus: OrderFulfilmentStatusType;
  orderProgressStatus: OrderProgressStatus;
  orderCollectionType: CollectionType;
  loyalty: Partial<BasketVerificationLoyalty>;
  delivery?: { trackingUrl: string; trackingUrlExpiresAt: string; fee: number };
}

export interface AddToAccountPayload {
  ordersWithLoyalty: string[];
  ordersToAssign: string[];
}

export interface AddToAccountResponse {
  assignedOrders: string[];
  skippedOrders: string[];
  loyaltyAwardedOrders: string[];
  emailMismatch: string[];
}

export interface RateOrderPayload {
  rating: number;
  brazeId: string;
  userFeedback: string;
  orderId: string;
}

export interface Surcharge {
  name: string;
  description: string;
  amount: number;
  surchargeAmount: number;
  surchargePercentage: number;
}
